/**
 * Grid container component
 * Creates and manages the 4x3 grid of cells
 */

import type { CellPosition, SimulationState } from '../types/simulation';
import { cellIdFromPosition } from '../types/simulation';
import { Cell } from './Cell';

export interface GridEvents {
  onCellClick?: (position: CellPosition) => void;
  onCellInitialize?: (position: CellPosition, canvas: HTMLCanvasElement) => void;
}

export class Grid {
  private container: HTMLDivElement;
  private cells: Map<string, Cell> = new Map();
  private events: GridEvents;

  readonly rows: number;
  readonly cols: number;

  constructor(
    containerElement: HTMLDivElement,
    rows: number = 3,
    cols: number = 4,
    events: GridEvents = {}
  ) {
    this.container = containerElement;
    this.rows = rows;
    this.cols = cols;
    this.events = events;

    this.createCells();
  }

  /**
   * Get cell by position
   */
  getCell(position: CellPosition): Cell | undefined {
    return this.cells.get(cellIdFromPosition(position));
  }

  /**
   * Get cell by ID
   */
  getCellById(id: string): Cell | undefined {
    return this.cells.get(id);
  }

  /**
   * Set active cell
   */
  setActive(position: CellPosition | null): void {
    for (const cell of this.cells.values()) {
      if (position && cell.position.row === position.row && cell.position.col === position.col) {
        cell.setActive(true);
      } else {
        cell.setActive(false);
      }
    }
  }

  /**
   * Set active cell by ID
   */
  setActiveById(id: string | null): void {
    for (const cell of this.cells.values()) {
      cell.setActive(cell.id === id);
    }
  }

  /**
   * Update cell state display
   */
  updateCellState(id: string, state: SimulationState): void {
    const cell = this.cells.get(id);
    if (cell) {
      cell.updateState(state);
    }
  }

  /**
   * Mark cell as initialized
   */
  markCellInitialized(position: CellPosition): void {
    const cell = this.getCell(position);
    cell?.markInitialized();
  }

  /**
   * Get all cell sizes for resize handling
   */
  getCellSizes(): Map<string, { width: number; height: number }> {
    const sizes = new Map<string, { width: number; height: number }>();
    for (const [id, cell] of this.cells) {
      sizes.set(id, cell.getSize());
    }
    return sizes;
  }

  /**
   * Clean up
   */
  dispose(): void {
    for (const cell of this.cells.values()) {
      cell.dispose();
    }
    this.cells.clear();
  }

  private createCells(): void {
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const position = { row, col };
        const cell = new Cell(position, {
          onClick: (pos) => this.events.onCellClick?.(pos),
          onInitialize: (pos, canvas) => this.events.onCellInitialize?.(pos, canvas),
        });

        this.cells.set(cell.id, cell);
        this.container.appendChild(cell.element);
      }
    }
  }
}
